import React, { useCallback, useEffect, useState } from 'react';
import { useOrdenTabla, ThOrdenable } from '../utils/ordenarTabla';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  getAgentStats, getConversion, getQualityByAgent, getTypificationByAgent, listUsersApi,
} from '../api';
import { formatNumber } from '../utils/format';
import DownloadButton from '../components/DownloadButton';
import AnalyticsFilterBar from '../components/AnalyticsFilterBar';
import { usePeticionVigente } from '../utils/peticionVigente';

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  // La concordancia de tipificación vive AQUÍ y no en Cumplimiento: esa boleta
  // es la métrica del supervisor y meterle esto le contamina la vista (Samuel,
  // 21-ago). Aquí son dos columnas más de la tabla que ya existe — "los datos
  // individuales pequeños", que es lo que pidió.
  const [conc, setConc] = useState({});
  // Calificación y conversión por agente (6-oct-2026). Salen de los MISMOS
  // endpoints que Análisis IA (`quality-by-agent`, con Score V2) y Conversión
  // (`conversion`), con los mismos filtros: un número por agente que difiera
  // entre pantallas es peor que no tenerlo. Cumplimiento no se pide aquí
  // porque abrirlo cuenta como visita en el registro de uso.
  const [calif, setCalif] = useState({});
  const [conv, setConv] = useState({});
  // Nombres de agente cuya CUENTA está dada de baja. Se marcan, y NO se
  // esconden ni se descuentan de nada.
  //
  // La lista viene de `users`; los números vienen de `calls`. Están separados a
  // propósito: si el estado de la cuenta entrara a las consultas de analytics,
  // dar de baja a los peores agentes subiría la conversión y la calificación
  // del equipo sin que nadie mejorara nada — y los números se verían mejor,
  // que es justo lo que alguien esperaba ver. La línea del equipo se calcula
  // sobre las LLAMADAS, así que el total no depende de cuántas filas se
  // muestren. Ver `backend/tests/test_baja_no_mueve_metricas.py`.
  //
  // Consecuencia asumida: un agente dado de baja sigue apareciendo mientras el
  // periodo consultado cubra sus llamadas. Desaparece solo cuando el periodo
  // avanza más allá de su última llamada. Si lo escondiéramos antes, las filas
  // visibles dejarían de sumar el total y la pantalla mentiría por omisión.
  const [inactivos, setInactivos] = useState(() => new Set());
  // `null` hasta que la barra emita: el estado inicial sin fechas consultaba
  // todo el histórico, y esa respuesta llegaba después y pisaba la filtrada.
  // Ver `utils/peticionVigente.js`.
  const [filters, setFilters] = useState(null);
  const nuevaPeticion = usePeticionVigente();
  // Orden en el cliente: esta tabla trae TODAS sus filas, así que ordenarlas
  // aquí es correcto. (En un listado paginado no lo sería — ver CallsExplorer.)
  const orden = useOrdenTabla(agents, {
    agent_name: (a) => a.agent_name,
    total_calls: (a) => a.total_calls,
    avg_duration_seconds: (a) => a.avg_duration_seconds,
    positive: (a) => a.sentiment_distribution?.positive || 0,
    neutral: (a) => a.sentiment_distribution?.neutral || 0,
    negative: (a) => a.sentiment_distribution?.negative || 0,
    // Concordancia: las claves salen del mapa `conc`, que se llena aparte.
    // Los que no tienen dato van al FINAL en las dos direcciones (-1), no al
    // principio: un agente sin concordancia arriba del ranking no dice nada.
    no_typing_rate: (a) => conc[a.agent_name]?.no_typing_rate ?? -1,
    agree_rate: (a) => (conc[a.agent_name]?.comparable ? conc[a.agent_name].agree_rate : -1),
    avg_score: (a) => calif[a.agent_name]?.avg_score ?? -1,
    v2_avg: (a) => calif[a.agent_name]?.v2_avg ?? -1,
    conv_rate: (a) => conv[a.agent_name]?.rate ?? -1,
  }, 'total_calls');

  const fetchAll = useCallback((params) => {
    const vigente = nuevaPeticion();
    setLoading(true);
    getAgentStats(params)
      .then(r => { if (vigente()) setAgents(r.data); })
      .catch(() => {})
      .finally(() => { if (vigente()) setLoading(false); });
    // Falla en silencio a propósito: si la concordancia no carga, las columnas
    // quedan en "-" y el resto de la tabla sigue sirviendo. Tumbar la pantalla
    // entera por dos columnas sería peor.
    getTypificationByAgent(params)
      .then((r) => {
        if (!vigente()) return;
        const porAgente = {};
        for (const a of r.data?.agents || []) porAgente[a.agent_name] = a;
        setConc(porAgente);
      })
      .catch(() => { if (vigente()) setConc({}); });
    // Igual que la concordancia: si fallan, sus columnas quedan en "-".
    getQualityByAgent(params)
      .then((r) => {
        if (!vigente()) return;
        const porAgente = {};
        for (const a of r.data?.agents || []) porAgente[a.agent_name] = a;
        setCalif(porAgente);
      })
      .catch(() => { if (vigente()) setCalif({}); });
    // `min_calls: 1`: aquí se ven TODOS los agentes de la tabla; el mínimo de
    // muestra se señala en la celda, no escondiendo la fila.
    getConversion({ ...params, group_by: 'agent', min_calls: 1 })
      .then((r) => {
        if (!vigente()) return;
        const porAgente = {};
        for (const g of r.data?.groups || []) porAgente[g.name] = g;
        setConv(porAgente);
      })
      .catch(() => { if (vigente()) setConv({}); });
  }, [nuevaPeticion]);

  // Una sola vez: no depende de los filtros del periodo.
  useEffect(() => {
    let vivo = true;
    listUsersApi()
      .then((r) => {
        if (!vivo) return;
        setInactivos(new Set(
          (r.data || [])
            .filter((u) => !u.is_active && u.agent_name)
            .map((u) => u.agent_name),
        ));
      })
      // Falla en silencio: sin la lista, las filas salen sin insignia y todos
      // los números siguen siendo los mismos. No hay nada que recalcular.
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  useEffect(() => { if (filters) fetchAll(filters); }, [filters, fetchAll]);

  // La barra va SIEMPRE en la misma posición (fuera del condicional de carga)
  // para no remontarla al alternar loading (si no, pierde estado y re-emite →
  // loop de carga).
  const chartData = agents.slice(0, 15).map(a => ({
    name: a.agent_name?.split(' ').slice(0, 2).join(' ') || 'N/A',
    calls: a.total_calls,
    avgDuration: Math.round(a.avg_duration_seconds),
  }));

  return (
    <div>
      <h1 className="page-title">Agentes</h1>
      <p className="page-subtitle">{formatNumber(agents.length)} agentes con llamadas procesadas</p>
      <AnalyticsFilterBar onChange={setFilters} />

      {loading ? (
        <div className="loading">Cargando agentes...</div>
      ) : (
        <>
          <div className="charts-grid">
            <div className="card">
              <div className="card-title">Llamadas por agente</div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} layout="vertical">
                  <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} width={130} />
                  <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8 }} />
                  <Bar dataKey="calls" fill="#818cf8" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <div className="card-title">Duracion promedio (seg)</div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} layout="vertical">
                  <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} width={130} />
                  <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #333', borderRadius: 8 }} />
                  <Bar dataKey="avgDuration" fill="#a78bfa" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20, marginBottom: 8 }}>
            <DownloadButton
              columns={[
                { key: 'agent_name', label: 'Agente' },
                { key: 'total_calls', label: 'Llamadas' },
                { key: 'avg_duration_seconds', label: 'Dur. prom. (s)' },
                { key: 'positive', label: 'Positivo' },
                { key: 'neutral', label: 'Neutral' },
                { key: 'negative', label: 'Negativo' },
                { key: 'avg_score', label: 'Score /100' },
                { key: 'v2_avg', label: 'Score V2 /10' },
                { key: 'conv_calls', label: 'Aprovechables' },
                { key: 'conv_sales', label: 'Ventas' },
                { key: 'conv_rate', label: 'Conversión %' },
              ]}
              rows={agents.map(a => ({
                agent_name: a.agent_name,
                total_calls: a.total_calls,
                avg_duration_seconds: Math.round(a.avg_duration_seconds),
                positive: a.sentiment_distribution.positive || 0,
                neutral: a.sentiment_distribution.neutral || 0,
                negative: a.sentiment_distribution.negative || 0,
                avg_score: calif[a.agent_name]?.avg_score ?? '',
                v2_avg: calif[a.agent_name]?.v2_avg ?? '',
                conv_calls: conv[a.agent_name]?.calls ?? '',
                conv_sales: conv[a.agent_name]?.conversions ?? '',
                conv_rate: conv[a.agent_name] ? (conv[a.agent_name].rate * 100).toFixed(1) : '',
              }))}
              filename="agentes"
            />
          </div>

          <div className="card" style={{ marginTop: 0, padding: 0, overflow: 'hidden' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <ThOrdenable columna="agent_name" orden={orden}>Agente</ThOrdenable>
                  <ThOrdenable columna="total_calls" orden={orden}>Llamadas</ThOrdenable>
                  <ThOrdenable columna="avg_duration_seconds" orden={orden}>Dur. prom.</ThOrdenable>
                  <ThOrdenable columna="positive" orden={orden}>Positivo</ThOrdenable>
                  <ThOrdenable columna="neutral" orden={orden}>Neutral</ThOrdenable>
                  <ThOrdenable columna="negative" orden={orden}>Negativo</ThOrdenable>
                  <ThOrdenable columna="avg_score" orden={orden}>Score</ThOrdenable>
                  <ThOrdenable columna="v2_avg" orden={orden}>Score V2</ThOrdenable>
                  <ThOrdenable columna="conv_rate" orden={orden}>Conversión</ThOrdenable>
                  {/* Concordancia de tipificación: el porcentaje se calcula
                      sobre las llamadas COMPARABLES, no sobre el total. Sobre
                      el total, un agente que casi no tipifica saldría bajo por
                      una razón distinta de la que se mide. */}
                  <ThOrdenable columna="no_typing_rate" orden={orden}>No tipificó</ThOrdenable>
                  <ThOrdenable columna="agree_rate" orden={orden}>Coincide</ThOrdenable>
                </tr>
              </thead>
              <tbody>
                {orden.filas.map(a => (
                  <tr key={a.agent_name}>
                    <td style={{ fontWeight: 600 }}>
                      <Link to={`/agents/${encodeURIComponent(a.agent_name)}`} style={{ color: '#a5b4fc', textDecoration: 'none' }}>{a.agent_name}</Link>
                      {inactivos.has(a.agent_name) && (
                        <span
                          style={{
                            marginLeft: 7, fontSize: 10.5, fontWeight: 700,
                            color: '#f87171', border: '1px solid #7f1d1d',
                            borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap',
                          }}
                          title="La cuenta está dada de baja: ya no tiene acceso. Sus llamadas del periodo siguen contando en los promedios del equipo."
                        >
                          inactivo
                        </span>
                      )}
                    </td>
                    <td>{formatNumber(a.total_calls)}</td>
                    <td>{Math.round(a.avg_duration_seconds)}s</td>
                    <td><span className="badge badge-positive">{formatNumber(a.sentiment_distribution.positive || 0)}</span></td>
                    <td><span className="badge badge-neutral">{formatNumber(a.sentiment_distribution.neutral || 0)}</span></td>
                    <td><span className="badge badge-negative">{formatNumber(a.sentiment_distribution.negative || 0)}</span></td>
                    <td><CeldaScore valor={calif[a.agent_name]?.avg_score} n={calif[a.agent_name]?.scored_count} decimales={1} /></td>
                    <td><CeldaScore valor={calif[a.agent_name]?.v2_avg} n={calif[a.agent_name]?.v2_calls} decimales={2} /></td>
                    <td><CeldaConversion g={conv[a.agent_name]} /></td>
                    <td><CeldaConcordancia c={conc[a.agent_name]} campo="no_typing" /></td>
                    <td><CeldaConcordancia c={conc[a.agent_name]} campo="agree" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


/** Promedio de calificación con su base en el hover. Sin base no hay número. */
function CeldaScore({ valor, n, decimales }) {
  if (valor == null) return <span style={{ color: '#4b5563' }}>-</span>;
  return (
    <span title={`Promedio de ${formatNumber(n || 0)} llamadas calificadas`} style={{ fontWeight: 600 }}>
      {valor.toFixed(decimales)}
    </span>
  );
}

// Mismo mínimo que la vista Conversión por omisión: con menos, una venta mueve
// la tasa decenas de puntos. No se esconde: se atenúa y se dice por qué.
const MIN_CONVERSION = 10;

/**
 * Conversión como en la vista Conversión sin tipificación elegida: ventas entre
 * llamadas APROVECHABLES (con diálogo), no sólo entre las útiles.
 */
function CeldaConversion({ g }) {
  if (!g || !g.calls) return <span style={{ color: '#4b5563' }}>-</span>;
  const pocas = g.calls < MIN_CONVERSION;
  return (
    <span
      title={`${formatNumber(g.conversions)} ventas de ${formatNumber(g.calls)} llamadas aprovechables`
        + (pocas ? ` · menos de ${MIN_CONVERSION}: muestra chica` : '')}
      style={{ fontWeight: 600, color: pocas ? '#6b7280' : undefined }}
    >
      {(g.rate * 100).toFixed(1)}%
    </span>
  );
}


/**
 * Una celda de concordancia: porcentaje, el crudo, y el margen cuando hace falta.
 *
 * El margen se muestra SÓLO con pocas comparables, y es Wilson y no Wald: con
 * Wald un agente con 4 de 4 salía "100.0% ±0.0", o sea certeza absoluta sobre
 * cuatro llamadas. Con Wilson sale ±24.5, que es lo honesto.
 */
function CeldaConcordancia({ c, campo }) {
  if (!c) return <span style={{ color: '#4b5563' }}>-</span>;

  if (campo === 'no_typing') {
    const pct = c.no_typing_rate;
    return (
      <span title={`${formatNumber(c.no_typing)} de ${formatNumber(c.total_calls)} llamadas`}>
        {pct == null ? '-' : `${(pct * 100).toFixed(1)}%`}
      </span>
    );
  }

  // Sin comparables no hay porcentaje que dar. Un "0%" ahí se leería como
  // "nunca coincide", que es una afirmación que los datos no sostienen.
  if (!c.comparable) return <span style={{ color: '#4b5563' }} title="Sin llamadas comparables">-</span>;

  const pct = (c.agree_rate * 100).toFixed(1);
  const margen = c.agree_margin != null ? (c.agree_margin * 100).toFixed(1) : null;
  const pocas = c.comparable < 30;
  return (
    <span title={`${formatNumber(c.agree)} de ${formatNumber(c.comparable)} comparables`}>
      <span style={{ fontWeight: 600 }}>{pct}%</span>
      {pocas && margen && (
        <span style={{ color: '#64748b', fontSize: 10, marginLeft: 4 }}>±{margen}</span>
      )}
    </span>
  );
}
